import { createSlice, createAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import { extractSharedActions, reducerUpsertHelper } from '@sda/utils/store';
import { Reducers } from '@sda/enums/store';
import { createEntityAdapter } from '@client/store/entity.adapter';
import { getDateTime } from '@sda/utils/date';
import type { GenericList } from '@sda/helpers/helpers.types';
import type {
  ShowBlastPayload, ShowDetailsPayload, ShowExternalDetailsPayload, ShowMetadata,
  ShowSplitPayload, ShowsState,
  SignoffPayload,
} from '@sda/interfaces/reducer/shows.reducer';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { PublicShow, Show } from '@sda/interfaces/show';
import type { FetchDataType } from '@sda/sdk/interfaces';
import type { UpsertItem } from '@sda/utils/store/interfaces';

// LISTENERS

export const startCancelShow = createAction<{ showId: string }>(`${Reducers.SHOWS}/startCancelShow`);
export const startDeleteShow = createAction<string>(`${Reducers.SHOWS}/startDeleteShow`);
export const startUpdateShow = createAction<{ showId: string, show: Partial<Show> }>(`${Reducers.SHOWS}/startUpdateShow`);
export const startSendShowBlastEmails = createAction<ShowBlastPayload>(`${Reducers.SHOWS}/startSendShowBlastEmails`);
export const startShareShow = createAction<{ showId: string, email?: string, connectionId?: string }>(`${Reducers.SHOWS}/startShareShow`);
export const startCreateSoloShow = createAction(`${Reducers.SHOWS}/startCreateSoloShow`);
export const startCreateRevenueSplitShow = createAction(`${Reducers.SHOWS}/startCreateRevenueSplitShow`);

/**
 * Threads
 */
export const startSaveDetailsSection = createAction<ShowDetailsPayload>(`${Reducers.SHOWS}/startSaveDetailsSection`);
export const startSaveExternalDetailsSection = createAction<ShowExternalDetailsPayload>(`${Reducers.SHOWS}/startSaveExternalDetailsSection`);
export const startSaveTicketsSection = createAction<object>(`${Reducers.SHOWS}/startSaveTicketsSection`);
export const startSaveSplitSection = createAction<ShowSplitPayload>(`${Reducers.SHOWS}/startSaveSplitSection`);
export const startSignoffShow = createAction<string>(`${Reducers.SHOWS}/startSignoffShow`);
export const startConvertTicketedShow = createAction<string>(`${Reducers.SHOWS}/startConvertTicketedShow`);
export const startSaveSignoffSection = createAction<SignoffPayload>(`${Reducers.SHOWS}/startSaveSignoffSection`);

/**
 * data fetching
 */
export const startFetchFullShow = createAction<string>(`${Reducers.SHOWS}/startFetchFullShow`);
export const startFetchShowDetails = createAction<string>(`${Reducers.SHOWS}/startFetchShowDetails`);
export const startFetchPublicShowDetails = createAction<string>(`${Reducers.SHOWS}/startFetchPublicShowDetails`);
export const startFetchMinimalShow = createAction<string>(`${Reducers.SHOWS}/startFetchMinimalShow`);
export const unsubscribeShow = createAction<string>(`${Reducers.SHOWS}/unsubscribeShow`);

export const defaultShowData = {
  allowEmailBlastsBeforeShowEnds: true,
  announceTime: null,
  createdAt: getDateTime().toJSDate(),
  updatedAt: getDateTime().toJSDate(),
  isIncomplete: true,
  hasSplitTicketSales: true,
  hasVenuePaymentAutomation: true,
  maximumPurchaseQuantity: parseFloat(process.env.MAX_TICKETS_PER_PURCHASE),
  confirmedAt: null,
  chatEnabled: true,
  isHyperwalletShow: true,
  signatureData: {},
  hasMultiPartySupport: true,
};

export const showAdapter = createEntityAdapter<Show & FetchDataType>({
  sortComparer: (a, b) => getDateTime(b.startTime).toMillis() - getDateTime(a.startTime).toMillis(),
});

const initialState: ShowsState = {
  metadata: null,
  current: null,
};

const showsSlice = createSlice({
  initialState: showAdapter.getInitialState<ShowsState>(initialState),
  name: Reducers.SHOWS,
  reducers: {
    ...extractSharedActions(showAdapter),
    setCurrentShow: (state, action: PayloadAction<string>) => {
      state.current = action.payload;
    },
    upsertShowMetadata: (state, action: PayloadAction<UpsertItem<ShowMetadata & FetchDataType>>) => {
      const metadata = reducerUpsertHelper(state.metadata, action.payload, 'showId');

      state.metadata = metadata;
    },
    upsertPublicShow: (state, action: PayloadAction<PublicShow & FetchDataType>) => {
      state.status = true;

      showAdapter.upsertOne(state, action.payload as Show & FetchDataType);
    },
    upsertPublicShows: (state, action: PayloadAction<GenericList<PublicShow & FetchDataType>>) => {
      state.status = true;

      if (!isEmpty(action.payload)) {
        showAdapter.upsertMany(state, action.payload as GenericList<Show & FetchDataType>);
      }
    },
  },
});

export const {
  removeOne: removeShow, upsertOne: upsertShow, upsertMany: upsertShows, updateOne: updateShow,
  setCurrentShow, upsertShowMetadata, upsertPublicShow, upsertPublicShows,
  addOne: addShow,
} = showsSlice.actions;

export default showsSlice.reducer;
